import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '@/design/components';
import { ModuleGate } from '@/access/Gate';
import { formatDate } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useSuggestMutations, type Suggestion } from './api';

/**
 * "הצע סידור" (PRD 5.5).
 *
 * שני שלבים נפרדים: ההצעה מחשבת ומציגה, והטעינה היא מה שנוגע בסידור.
 * ההפרדה אינה טקס. ההצעה היא ניחוש מושכל, והמנהל הוא זה שמחליט,
 * ולכן אסור שהלחיצה הראשונה תשנה לו את הלוח.
 *
 * מאחורי שער המודול: רשת שלא רכשה את התוספת לא רואה את הכפתור.
 */
export function SuggestButton({
  periodId,
  shiftNames,
  onApplied,
}: {
  periodId: string;
  /** שם הרצועה לפי מזהה, להצגת החוסרים בשפה של המנהל */
  shiftNames: Record<string, string>;
  onApplied: (added: number) => void;
}) {
  return (
    <ModuleGate module="ai_schedule" fallback={null}>
      <SuggestButtonInner
        periodId={periodId}
        shiftNames={shiftNames}
        onApplied={onApplied}
      />
    </ModuleGate>
  );
}

function SuggestButtonInner({
  periodId,
  shiftNames,
  onApplied,
}: {
  periodId: string;
  shiftNames: Record<string, string>;
  onApplied: (added: number) => void;
}) {
  const { suggest, apply } = useSuggestMutations(periodId);
  const [result, setResult] = useState<Suggestion | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <Button
        variant="secondary"
        disabled={suggest.isPending}
        onClick={() => {
          setError(null);
          suggest
            .mutateAsync()
            .then(setResult)
            .catch((caught) => setError(errorMessage(caught)));
        }}
      >
        <Sparkles size={20} strokeWidth={1.75} aria-hidden="true" />
        {suggest.isPending ? 'בונה הצעה...' : 'הצעת סידור'}
      </Button>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {result && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="הצעת סידור"
          className="fixed inset-0 z-40 grid place-items-center bg-charcoal/50 p-4"
        >
          <div className="grid max-h-[85dvh] w-full max-w-md gap-3 overflow-y-auto rounded-lg bg-surface p-4">
            <h2 className="m-0 font-display text-display-m font-semibold">הצעת סידור</h2>

            <p className="m-0 text-body text-ink">{result.explanation}</p>

            {result.shortages.length > 0 && (
              <div className="grid gap-1">
                <h3 className="m-0 text-label font-medium text-muted">משבצות שנשארו ריקות</h3>
                <ul className="m-0 grid list-none gap-1 p-0">
                  {result.shortages.map((gap) => (
                    <li
                      key={`${gap.date}-${gap.shift_type_id}`}
                      className="flex items-center justify-between gap-2 text-body-sm"
                    >
                      <span>
                        {formatDate(gap.date)} · {shiftNames[gap.shift_type_id] ?? 'משמרת'}
                      </span>
                      <span className="text-muted">חסרים {gap.missing}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="m-0 text-body-sm text-muted">
              הטעינה מוסיפה את השיבוצים כטיוטה. אפשר לערוך אחר כך, והסידור לא מתפרסם עד
              שתלחץ/י על פרסום.
            </p>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={apply.isPending || result.filled === 0}
                onClick={() => {
                  setError(null);
                  apply
                    .mutateAsync(result.run_id)
                    .then((added) => {
                      setResult(null);
                      onApplied(added);
                    })
                    .catch((caught) => setError(errorMessage(caught)));
                }}
              >
                טעינה כטיוטה
              </Button>
              <Button variant="secondary" onClick={() => setResult(null)}>
                ביטול
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
